import { constants } from 'config/constants';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import { saveBlob } from 'library/helpers/saveBlob';
import SuperFetch, { postForBlob } from 'library/helpers/superFetch';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { reportsActions } from './slice';

// The only two formats the server writes. CSV appears in the design mock-up and is NOT offered by
// the API (docs/domains/reports/api-contract.md § Enums) — see EXPORT_FORMATS in the modal.
const EXTENSION_FOR_FORMAT = { xlsx: '.xlsx', pdf: '.pdf' };

export function* loadReportTypes() {
	try {
		const res = yield call(SuperFetch.get, '/reports/types');
		if (res.statusCode === 200) {
			yield put(reportsActions.loadReportTypesSuccess(res.data || []));
		} else {
			throw Error(apiFailureMessage(res, 'Failed to load the report catalogue.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.loadReportTypesError(message));
		Notification('error', message);
	}
}

/**
 * The categories a locker report may be scoped to.
 *
 * 🛑 `scope.areaIDs` carries **categoryID**, not assetGroup `groupID`. Sending a groupID comes back
 * as a FOREIGN id and the write is rejected — which is why this replaced the device-groups list the
 * wizard used to read.
 *
 * Server-filtered to the two locker default categories (`BOOKEABLE_LOCKERS` = 14 Smart Locker,
 * 19 Zezamii Lockers) as REPEATED query params — `?defaultCategoryID=14&defaultCategoryID=19` — the
 * only form the route parses (it validates as `id | array(id)`, so a comma-joined value is refused;
 * see `buildQueryString`). Filtering here rather than client-side keeps a non-locker category from
 * ever reaching the picker.
 *
 * ⚠️ An EMPTY picker is not necessarily a bug here, and one known cause is not fixable from the
 * dashboard: a SITE-scoped role resolves zero areas because locker categories carry no SiteID
 * binding in `Device.AssociateCategory`, so every areaID it could send comes back `foreign`. Global
 * and workspace roles are unaffected.
 *
 * 🛑 A non-admin is NOT blocked from reading this list — `requireCapability` returns early for every
 * safe method, so it has only ever gated writes. An earlier revision of the reports handover said
 * this route was admin-only; it was wrong and was retracted (App Server 3745a89f). The 403 branch
 * below stays as the generic failure path it always was, but do not read it as the expected answer
 * for a non-admin: if a 403 ever reproduces on this GET it is coming from roleMiddleware or the
 * org's module flag, and belongs traced there.
 */
export function* loadReportAreas() {
	try {
		const res = yield call(
			SuperFetch.get,
			'/deviceCategories',
			{},
			{ defaultCategoryID: constants.BOOKEABLE_LOCKERS }
		);
		if (res.statusCode === 200) {
			yield put(reportsActions.loadReportAreasSuccess(res.data || []));
		} else {
			throw Error(apiFailureMessage(res, 'Failed to load the areas this report can cover.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.loadReportAreasError(message));
		Notification('error', message);
	}
}

export function* loadReports() {
	try {
		const res = yield call(SuperFetch.get, '/reports');
		if (res.statusCode === 200) {
			yield put(reportsActions.loadReportsSuccess(res.data || []));
		} else {
			throw Error(apiFailureMessage(res, 'Failed to load scheduled reports.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.loadReportsError(message));
		Notification('error', message);
	}
}

/**
 * Generate and save a report file NOW.
 *
 * 🛑 `/reports/download` answers with the FILE, not the v5 envelope — `postForBlob` branches on
 * the status code and only parses JSON on a failure. The status code says WHICH failure it is, and
 * the two need opposite handling:
 *   · **400 — the operator can fix it.** A foreign area, or the over-cap refusal: "This report
 *     matches 8123 rows, above the 5000 limit — narrow the scope". Surfaced VERBATIM (`getApiError`
 *     prefers `info`/`error` over the generic `message`) and the wizard stays open on the scope.
 *   · **500 — retry the SAME request.** The file is stored in Azure before it is returned, so a 500
 *     can mean "generated but not stored" and pressing Export again is the right answer. Its body is
 *     suppressed by `apiFailureMessage` on purpose: a 5xx `error` is usually a raw `toString()`.
 *     🛑 Do NOT add a reports exception to that rule — the over-cap message was moved OFF 500 and
 *     onto 400 server-side (23 Sept) precisely so the global suppression could stay.
 *
 * ⚠️ `Content-Disposition` is only readable cross-origin when the server exposes it, and the
 * dashboard is always a different origin from the App Server — so the caller's `filename` is the
 * one that normally lands. The server's name is preferred when it IS readable, because it is the
 * name the run ledger and the audit trail recorded.
 */
export function* downloadReport({ payload = {} }) {
	const { reportTypeID, format = 'xlsx', scope, filename } = payload;
	try {
		const res = yield call(postForBlob, '/reports/download', { reportTypeID, format, scope });

		if (res.statusCode === 200 && res.blob) {
			const fallback = `${filename || 'lockers-report'}${EXTENSION_FOR_FORMAT[format] || ''}`;
			yield call(saveBlob, res.blob, res.filename || fallback);
			yield put(reportsActions.downloadReportSuccess());
			Notification('success', 'Your report has been downloaded.');
			return;
		}
		throw Error(apiFailureMessage(res, 'Failed to generate the report.'));
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.downloadReportError(message));
		Notification('error', message);
	}
}

export function* createReport({ payload = {} }) {
	try {
		const res = yield call(SuperFetch.post, '/reports', payload);
		// This surface answers 200 on create (`{ statusCode, message, info, data: { reportID } }`),
		// unlike POST /deviceCategories which answers 201. Both are accepted so a server that ever
		// switches to the other convention does not turn a written schedule into a red toast.
		if (res.statusCode === 200 || res.statusCode === 201) {
			yield put(reportsActions.createReportSuccess());
			Notification('success', 'Scheduled report created.');
			yield put(reportsActions.loadReports());
		} else {
			// The responder String()s a list of Joi messages, so a two-problem failure arrives as
			// one comma-joined sentence. Shown whole — splitting it guesses at where the seams are.
			throw Error(apiFailureMessage(res, 'Failed to create the scheduled report.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.createReportError(message));
		Notification('error', message);
	}
}

/**
 * PATCH one report. `patch` carries only the fields being changed — the server keeps the rest
 * (every field is guarded on `!== undefined`), so omitting `recipients` leaves them untouched.
 * 🛑 `reportTypeID` is NOT patchable (a 400); the caller must never put it in `patch`.
 *
 * 🛑 `recipients` REPLACES the set — it does not merge. The server DELETEs every stored address not
 * in the list and INSERTs the ones missing, so the list sent becomes the set and an address the
 * operator removed IS removed. Callers must therefore send the FULL desired list, never a delta.
 *
 * ⚠️ There is NO concurrency guarantee, contrary to what this comment claimed until 23 Sept
 * (confirmed against the App Server's `upsertRecipients`): two admins PATCHing `recipients` at the
 * same time do not merge — the last commit wins the whole set. Row-by-row writing buys something
 * else, which is that an unchanged address is never rewritten, so the audit trail names exactly who
 * was added or removed. Protecting against the lost update would have to be optimistic concurrency
 * at THIS layer; the server does not offer it.
 */
export function* updateReport({ payload = {} }) {
	const { reportID, patch } = payload;
	try {
		const res = yield call(SuperFetch.patch, `/reports/${reportID}`, patch);
		if (res.statusCode === 200) {
			yield put(reportsActions.updateReportSuccess());
			// PATCH answers with no `data`, so the row on screen is only as fresh as a re-fetch.
			yield put(reportsActions.loadReports());
		} else {
			throw Error(apiFailureMessage(res, 'Failed to update the scheduled report.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.updateReportError(message));
		Notification('error', message);
	}
}

export function* deleteReport({ payload }) {
	try {
		const res = yield call(SuperFetch.delete, `/reports/${payload}`);
		if (res.statusCode === 200) {
			yield put(reportsActions.deleteReportSuccess());
			Notification('success', 'Scheduled report deleted.');
			yield put(reportsActions.loadReports());
		} else {
			throw Error(apiFailureMessage(res, 'Failed to delete the scheduled report.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(reportsActions.deleteReportError(message));
		Notification('error', message);
	}
}

export default function* reportsSaga() {
	yield takeLatest(reportsActions.loadReportTypes, loadReportTypes);
	yield takeLatest(reportsActions.loadReportAreas, loadReportAreas);
	yield takeLatest(reportsActions.loadReports, loadReports);
	yield takeLatest(reportsActions.downloadReport, downloadReport);
	yield takeLatest(reportsActions.createReport, createReport);
	// takeEvery, not takeLatest: the manage dialog can toggle two schedules in quick succession and
	// each PATCH targets a DIFFERENT reportID — takeLatest would cancel the first one's re-fetch
	// and, worse, its pending request, leaving a row showing a state the server never received.
	yield takeEvery(reportsActions.updateReport, updateReport);
	yield takeEvery(reportsActions.deleteReport, deleteReport);
}
